import { domainOperations } from "@workerstation/shared/effect/operation";
import { Effect, Schema } from "effect";
import { AgenticCacheResult, AgenticTracePhase } from "../agentic-correlation.ts";
import { FlowRecordingMode, FlowRecordingState } from "../flow-recording-model.ts";
import { recordingInvalid } from "../errors/recording.ts";
import { RecordingQuery } from "./model.ts";

const domainOperation = domainOperations("Recording");

export const recordingNumberParam = domainOperation("queryNumber")(function* (search: URLSearchParams, name: string, minimum: number, maximum: number) {
  const raw = search.get(name);
  if (raw === null || raw === "") return undefined;
  return yield* Schema.decodeUnknownEffect(Schema.Int.check(Schema.isBetween({ minimum, maximum })))(Number(raw)).pipe(
    Effect.mapError(() => recordingInvalid(`${name} must be an integer between ${minimum} and ${maximum}.`)));
});
const textParam = domainOperation("queryText")(function* (search: URLSearchParams, name: string, maximum: number) {
  const text = search.get(name)?.trim();
  if (!text) return undefined;
  return yield* Schema.decodeUnknownEffect(Schema.String.check(Schema.isMaxLength(maximum)))(text).pipe(
    Effect.mapError(() => recordingInvalid(`${name} must be at most ${maximum} characters.`)));
});
const enumParam = domainOperation("queryEnum")(function* <A extends string>(search: URLSearchParams, name: string, schema: Schema.Literals<readonly A[]>) {
  const value = search.get(name);
  if (value === null || value === "") return undefined;
  return yield* Schema.decodeUnknownEffect(schema)(value).pipe(
    Effect.mapError(() => recordingInvalid(`${name} must be one of: ${schema.literals.join(", ")}.`)));
});
export const recordingListQuery = domainOperation("listQuery")(function* (search: URLSearchParams) {
  const fields = { mode: yield* enumParam(search, "mode", FlowRecordingMode), state: yield* enumParam(search, "state", FlowRecordingState),
    sessionId: yield* textParam(search, "sessionId", 256), flowName: yield* textParam(search, "flowName", 200),
    since: yield* recordingNumberParam(search, "since", 0, Number.MAX_SAFE_INTEGER), before: yield* recordingNumberParam(search, "before", 0, Number.MAX_SAFE_INTEGER),
    limit: yield* recordingNumberParam(search, "limit", 1, 200) };
  const context = yield* Effect.forEach(["changeId", "taskId", "runId", "parentRunId", "attemptId", "worktreeId", "treeId",
    "inputTreeId", "outputTreeId", "candidateId", "checkId", "reviewId", "namespaceId"], key =>
    textParam(search, key, 256).pipe(Effect.map(value => [key, value] as const)), { concurrency: 1 });
  return yield* Schema.decodeUnknownEffect(RecordingQuery)(Object.fromEntries(Object.entries({ ...fields, ...Object.fromEntries(context),
    fence: yield* recordingNumberParam(search, "fence", 1, Number.MAX_SAFE_INTEGER), cacheResult: yield* enumParam(search, "cacheResult", AgenticCacheResult),
    eventSequence: yield* recordingNumberParam(search, "eventSequence", 0, Number.MAX_SAFE_INTEGER), targetRef: yield* textParam(search, "targetRef", 128),
    phase: yield* enumParam(search, "phase", AgenticTracePhase)
  }).filter(([, value]) => value !== undefined))).pipe(Effect.mapError(error => recordingInvalid(error.message)));
});

export const recordingRange = (header: string | null): { offset: number; length?: number } | undefined | "invalid" => {
  if (!header) return undefined;
  const matched = /^bytes=(\d+)-(\d*)$/.exec(header.trim());
  if (!matched) return "invalid";
  const offset = Number(matched[1]);
  if (!Number.isSafeInteger(offset)) return "invalid";
  if (matched[2] === "") return { offset };
  const end = Number(matched[2]);
  if (!Number.isSafeInteger(end) || end < offset) return "invalid";
  return { offset, length: end - offset + 1 };
};
